import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    FormControl,
    InputLabel,
    MenuItem,
    MenuItemProps,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    styled,
} from '@mui/material';
import React from 'react';
import { FieldError } from 'react-hook-form';
import { EColors, getColor } from 'theme';

const ITEM_HEIGHT = 34;
const ITEM_COUNT = 5;
// Explicit z-index keeps the menu above any modal/drawer it may be nested in (e.g. RightDrawer,
// which sits above `theme.zIndex.modal` to clear the side nav — see RightDrawer.tsx). Without
// this, the menu's default z-index (theme.zIndex.modal) can render behind an elevated container.
const MENU_Z_INDEX = 1350;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * ITEM_COUNT,
            borderRadius: 12,
        },
    },
    sx: { zIndex: MENU_Z_INDEX },
};

const StyledFormControl = styled(FormControl)<{ disabled?: boolean }>(({ disabled }) => ({
    width: '100%',
    backgroundColor: disabled ? getColor(EColors.drWhite) : getColor(EColors.pureWhite),
    borderRadius: '12px',
    pointerEvents: disabled ? 'none' : 'auto',

    '&:hover .MuiInputBase-root:hover > .MuiOutlinedInput-notchedOutline': {
        borderColor: getColor(EColors.v3Dark) + ' !important',
    },
    '& .MuiFormLabel-root': {
        transform: 'translate(16px, 14px)',
        fontSize: '0.875rem',
        lineHeight: '1.5em',
        color: getColor(EColors.pureBlack, 0.5),
    },

    '& .MuiFormLabel-root.MuiInputLabel-shrink': {
        color: getColor(EColors.pureBlack, 0.6),
        fontSize: '0.875rem',
        transform: 'translate(9px, -12px) scale(1)',
        backgroundColor: getColor(EColors.pureWhite),
        padding: '0 5px',
        lineHeight: '1rem',
    },
}));

const StyledSelect = styled(Select<string>, {
    shouldForwardProp: prop =>
        prop !== 'disabled' && prop !== 'error' && prop !== 'hasPlaceholder' && prop !== 'hideColors',
})<{ disabled?: boolean; hasPlaceholder?: boolean; hideColors?: boolean }>(
    ({ disabled, error, hasPlaceholder, hideColors }) => ({
        pointerEvents: disabled ? 'none' : 'auto',
        '& .MuiOutlinedInput-notchedOutline': {
            border: hideColors ? 'none' : `1px solid ${error ? getColor(EColors.red) : getColor(EColors.v3Border)}`,
            borderRadius: '12px',
        },

        ...(hideColors && {
            '&.MuiOutlinedInput-root.MuiOutlinedInput-root:hover > .MuiOutlinedInput-notchedOutline': {
                boxShadow: 'none',
            },
            '&.MuiOutlinedInput-root.MuiOutlinedInput-root.Mui-focused > .MuiOutlinedInput-notchedOutline': {
                boxShadow: 'none',
            },
        }),

        '& .MuiOutlinedInput-input': {
            backgroundColor: disabled ? getColor(EColors.drWhite) : getColor(EColors.v3Background),
            borderRadius: '12px',
            padding: '12.5px',
            paddingLeft: '16px',
            fontSize: '0.875rem',
            color: hasPlaceholder ? getColor(EColors.pureBlack, 0.5) : getColor(EColors.pureBlack),
        },

        '&.Mui-disabled': {
            color: getColor(EColors.pureBlack, 0.5),
            opacity: 0.5,

            '& .MuiSelect-select': {
                color: getColor(EColors.pureBlack),
                WebkitTextFillColor: getColor(EColors.pureBlack),
            },

            '& .MuiMenuItem-root.Mui-disabled': {
                color: getColor(EColors.pureBlack),
            },
        },
    })
);

const StyledInputLabel = styled(InputLabel)({
    fontSize: '0.875rem',
    lineHeight: '0.875rem',

    '&.MuiFormLabel-root': {
        top: '1.5px',
        color: getColor(EColors.pureBlack, 0.5),
    },

    '&.MuiInputLabel-shrink': {
        lineHeight: '0.75rem',
        top: 4,
    },
});

const StyledMenuItem = styled(MenuItem)<MenuItemProps>(({ theme: { spacing } }) => ({
    margin: spacing(0, 1.5),
    padding: spacing(1),
    fontSize: '0.75rem',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    color: 'inherit',
    '&:hover': {
        backgroundColor: getColor(EColors.pureBlack, 0.1),
        color: getColor(EColors.pureBlack),
    },
    '&.Mui-selected': {
        backgroundColor: getColor(EColors.pureBlack),
        color: getColor(EColors.pureWhite),
    },
    '&.Mui-selected:hover': {
        backgroundColor: getColor(EColors.pureBlack, 0.9),
        color: getColor(EColors.pureWhite),
    },
    '&.Mui-focusVisible.Mui-selected': {
        backgroundColor: getColor(EColors.pureBlack, 0.9),
    },
}));

type OptionType = { value: number | string; label: string };

type SelectFieldProps = {
    label?: string;
    error?: FieldError;
    value?: string | number;
    disabled?: boolean | undefined;
    required?: boolean;
    options: OptionType[];
    onChange?: (value?: string | number) => void;
    onBlur?: (e: SelectChangeEvent<string>) => void;
    placeholder?: string;
    hideColors?: boolean;
};

export const SelectField = React.forwardRef<HTMLDivElement, SelectFieldProps>(
    ({ label, value, options, required, onChange, placeholder, error, hideColors = false, ...rest }, ref) => {
        const [open, setOpen] = React.useState(false);
        const showPlaceholder = !value && open && !!placeholder;

        const handleChange = (event: SelectChangeEvent<string>) => {
            event.stopPropagation();
            onChange?.(event.target.value);
        };

        const handleOpen = () => {
            setOpen(true);
        };

        const handleClose = () => {
            setOpen(false);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            rest.onBlur?.(e as unknown as SelectChangeEvent<string>);
        };

        const displayValue = showPlaceholder && !value ? '' : value?.toString();

        return (
            <StyledFormControl ref={ref} required={required}>
                <StyledInputLabel shrink={open || !!value}>{label}</StyledInputLabel>
                <StyledSelect
                    IconComponent={KeyboardArrowDownIcon}
                    value={displayValue}
                    onChange={handleChange}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onBlur={handleBlur}
                    open={open}
                    input={<OutlinedInput label={open || value ? label : ''} />}
                    MenuProps={MenuProps}
                    displayEmpty={!!placeholder}
                    hasPlaceholder={showPlaceholder}
                    error={!!error}
                    hideColors={hideColors}
                    renderValue={(selected?: string) => {
                        if (showPlaceholder && !selected) {
                            return placeholder;
                        }
                        const selectedOption = options.find(option => option.value.toString() === selected?.toString());
                        if (selectedOption) {
                            return selectedOption.label;
                        }
                        return selected;
                    }}
                    {...rest}
                >
                    {options.map(option => (
                        <StyledMenuItem key={option.value} value={option.value}>
                            {option.label}
                        </StyledMenuItem>
                    ))}
                </StyledSelect>
            </StyledFormControl>
        );
    }
);
